// Foreva Jewels — product card metal swatches and ready-to-ship badge
// (markup: snippets/product-card-metal-swatches.liquid, snippets/fj-ready-to-ship.liquid).
//
// One set of delegated listeners for the whole page, so cards added later by filtering,
// "load more" or section re-renders work without re-initialising anything.
//   Desktop: hovering a colour dot swaps the card photo and opens that colour's karat popup;
//            clicking the dot follows its link (the 10K product).
//   Touch:   the first tap on a dot swaps the photo and opens the popup; a second tap opens the product.
// The other colours' photos are only downloaded the first time a shopper reaches that card's dots.
(() => {
  if (window.__fjCardSwatches) return;
  window.__fjCardSwatches = true;

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  /** @type {HTMLElement | null} */
  let openSet = null;

  /** @param {HTMLElement} set */
  const galleryFor = (set) => set.closest('product-card')?.querySelector('.card-gallery') ?? null;

  /**
   * One overlay <img> per colour, created once and kept. (Swapping one image's srcset and waiting on
   * decode() raced: the browser aborts a decode started before it picks up the new source, so the
   * photo sometimes never showed.)
   * @param {HTMLElement} gallery @param {HTMLElement} dot
   */
  function overlayFor(gallery, dot) {
    const c = dot.dataset.c || '';
    let img = /** @type {HTMLImageElement | null} */ (gallery.querySelector(`:scope > img.fj-sw-overlay[data-c="${c}"]`));
    if (!img) {
      img = document.createElement('img');
      img.className = 'fj-sw-overlay';
      img.alt = '';
      img.decoding = 'async';
      img.dataset.c = c;
      img.sizes = gallery.querySelector('img[sizes]')?.getAttribute('sizes') || '(min-width: 750px) 33vw, 50vw';
      img.srcset = dot.dataset.srcset || '';
      gallery.append(img);
    }
    return img;
  }

  /** Start loading the other colours' photos when a shopper reaches a card's dots. @param {HTMLElement} set */
  function preload(set) {
    if (set.dataset.fjPreloaded) return;
    set.dataset.fjPreloaded = '1';
    const gallery = galleryFor(set);
    if (!gallery) return;
    for (const dot of set.querySelectorAll('.fj-sw__dot[data-srcset]:not([aria-current="true"])')) overlayFor(gallery, /** @type {HTMLElement} */ (dot));
  }

  /** @param {HTMLElement} set @param {HTMLElement | null} dot */
  function showPhoto(set, dot) {
    const gallery = galleryFor(set);
    if (!gallery) return;
    const want = dot && dot.getAttribute('aria-current') !== 'true' && dot.dataset.srcset ? dot.dataset.c : '';
    set.dataset.fjShowing = want || '';
    for (const img of gallery.querySelectorAll(':scope > img.fj-sw-overlay')) {
      if (img instanceof HTMLImageElement && img.dataset.c !== want) img.classList.remove('is-on');
    }
    if (!want || !dot) return;
    const img = overlayFor(gallery, dot);
    const reveal = () => { if (set.dataset.fjShowing === want) img.classList.add('is-on'); };
    if (img.complete && img.naturalWidth) reveal();
    else img.addEventListener('load', reveal, { once: true });
  }

  /** @param {HTMLElement} set @param {HTMLElement} dot */
  function activate(set, dot) {
    if (openSet && openSet !== set) reset(openSet);
    openSet = set;
    set.classList.add('is-browsing');
    for (const d of set.querySelectorAll('.fj-sw__dot')) d.classList.toggle('is-active', d === dot);
    for (const p of set.querySelectorAll('.fj-sw__pop')) p.hidden = p.dataset.for !== dot.dataset.c;
    const pop = set.querySelector(`.fj-sw__pop[data-for="${dot.dataset.c}"]`);
    if (pop) {
      // Centre the popup on the dot, but keep it inside the card; the arrow still points at the dot.
      const centre = dot.offsetLeft + dot.offsetWidth / 2;
      const room = (set.closest('product-card') || set).clientWidth;
      const left = Math.max(0, Math.min(centre - pop.offsetWidth / 2, room - pop.offsetWidth));
      pop.style.setProperty('--fj-x', `${left}px`);
      pop.style.setProperty('--fj-arrow', `${centre - left}px`);
    }
    const name = set.querySelector('.fj-sw__name');
    if (name) name.textContent = dot.dataset.name || '';
    showPhoto(set, dot);
  }

  /** @param {HTMLElement} set */
  function reset(set) {
    set.classList.remove('is-browsing');
    for (const d of set.querySelectorAll('.fj-sw__dot')) d.classList.remove('is-active');
    for (const p of set.querySelectorAll('.fj-sw__pop')) p.hidden = true;
    const name = set.querySelector('.fj-sw__name');
    if (name) name.textContent = set.dataset.start || '';
    showPhoto(set, null);
    if (openSet === set) openSet = null;
  }

  const dotFrom = (target) => (target instanceof Element ? target.closest('.fj-sw__dot') : null);
  const setFrom = (target) => (target instanceof Element ? target.closest('[data-fj-sw]') : null);

  if (canHover) {
    document.addEventListener('pointerover', (event) => {
      const set = setFrom(event.target);
      if (!set) return;
      preload(set);
      const dot = dotFrom(event.target);
      if (dot) activate(set, dot);
    });
    document.addEventListener('pointerout', (event) => {
      const set = setFrom(event.target);
      if (set && !set.contains(/** @type {Node | null} */ (event.relatedTarget))) reset(set);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;

    // Ready-to-ship badge: tap toggles the note on touch screens
    const rts = target instanceof Element ? target.closest('.fj-rts') : null;
    for (const open of document.querySelectorAll('.fj-rts.is-open')) if (open !== rts) open.classList.remove('is-open');
    if (rts) {
      event.preventDefault();
      event.stopPropagation();
      if (!canHover) rts.classList.toggle('is-open');
      return;
    }

    const dot = dotFrom(target);
    const set = setFrom(target);
    if (dot && set && !canHover && !dot.classList.contains('is-active')) {
      event.preventDefault();
      event.stopPropagation();
      preload(set);
      activate(set, dot);
      return;
    }
    if (dot || (set && target instanceof Element && target.closest('.fj-sw__karat'))) {
      event.stopPropagation(); // let the link navigate without the card's own click handling
      return;
    }
    if (openSet && !set) reset(openSet);
  }, true);

  document.addEventListener('focusin', (event) => {
    const dot = dotFrom(event.target);
    const set = setFrom(event.target);
    if (dot && set && dot.matches(':focus-visible')) {
      preload(set);
      activate(set, dot);
    }
  });

  document.addEventListener('focusout', (event) => {
    const set = setFrom(event.target);
    if (set && !set.contains(/** @type {Node | null} */ (event.relatedTarget))) reset(set);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openSet) reset(openSet);
  });
})();
